import { useMemo, useState } from 'react'
import { decodeFilters } from 'app/filter.utils'
import { useListTraversal } from 'app/hooks/useListTraversal'
import type { ContactJson } from 'app/types/entities'
import { notifyError } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import { useContactStore } from 'app/stores/StoreContext'

type UseContactListModalOptions = {
  /** All contacts before any client-side filtering */
  allContacts: ContactJson[]
  /** Callback when a URL param should be updated */
  onUrlParamChange?: (key: string, value: string | null) => void
}

/**
 * Container hook for ContactListModal.
 * Encapsulates search, filter, drawer, and traversal state.
 */
export function useContactListModal({ allContacts, onUrlParamChange }: UseContactListModalOptions) {
  const [searchValue, setSearchValue] = useState(``)
  const [filterValue, setFilterValue] = useState(``)
  const [selectedContact, setSelectedContact] = useState<ContactJson>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const contactStore = useContactStore()

  const searchedContacts = useMemo((): ContactJson[] => {
    if (!searchValue) return allContacts
    const query = searchValue.toLowerCase()
    return allContacts.filter((c) => {
      const phone = c.cellPhone || c.homePhone || c.workPhone || ``
      return (
        c.name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        phone.includes(query) ||
        c.lifecycleStage?.toLowerCase().includes(query)
      )
    })
  }, [allContacts, searchValue])

  const filteredContacts = useMemo((): ContactJson[] => {
    if (!filterValue) return searchedContacts
    const filters = decodeFilters(filterValue)
    let result = searchedContacts
    for (const filter of filters) {
      const attr = filter.attribute.replace(`.keyword`, ``)
      result = result.filter((c) => {
        const raw = c[attr as keyof ContactJson]
        const isArray = Array.isArray(raw)
        const strVal = isArray ? `` : (typeof raw === `string` ? raw : ``).toLowerCase()
        const arrVal: string[] = isArray ? (raw as string[]).map((v) => v.toLowerCase()) : []

        switch (filter.operator) {
          case `exactly`:
          case `not_exactly`: {
            const target = String(filter.value).toLowerCase()
            const match = isArray ? arrVal.includes(target) : strVal === target
            return filter.operator === `exactly` ? match : !match
          }
          case `contains`:
          case `not_contains`: {
            const target = String(filter.value).toLowerCase()
            const match = isArray ? arrVal.some((v) => v.includes(target)) : strVal.includes(target)
            return filter.operator === `contains` ? match : !match
          }
          case `in`: {
            if (!Array.isArray(filter.value)) return true
            const targets = filter.value.map((v) => v.toLowerCase())
            return isArray ? targets.some((t) => arrVal.includes(t)) : targets.includes(strVal)
          }
          case `not_in`: {
            if (!Array.isArray(filter.value)) return true
            const targets = filter.value.map((v) => v.toLowerCase())
            return isArray ? !targets.some((t) => arrVal.includes(t)) : !targets.includes(strVal)
          }
          case `between`: {
            if (!Array.isArray(filter.value) || filter.value.length < 2) return true
            const val = raw ? new Date(raw as string).getTime() : NaN
            if (isNaN(val)) return false
            const from = new Date(filter.value[0]).getTime()
            // Treat the upper bound as end-of-day so dates with a time
            // component on the last day of the range are included.
            const toDate = new Date(filter.value[1])
            toDate.setHours(23, 59, 59, 999)
            const to = toDate.getTime()
            return val >= from && val <= to
          }
          case `gte`: {
            const val = raw ? new Date(raw as string).getTime() : NaN
            if (isNaN(val)) return false
            return val >= new Date(String(filter.value)).getTime()
          }
          case `lte`: {
            const val = raw ? new Date(raw as string).getTime() : NaN
            if (isNaN(val)) return false
            return val <= new Date(String(filter.value)).getTime()
          }
          default:
            return true
        }
      })
    }
    return result
  }, [searchedContacts, filterValue])

  const traversal = useListTraversal(filteredContacts, selectedContact, (contact) => {
    setSelectedContact(contact)
    onUrlParamChange?.(`contact`, contact.id!)
  })

  async function handleContactClick(contactId: string): Promise<void> {
    await contactStore.fetchOne(contactId)
    const response = contactStore.current
    if (!response || (response as any).error) {
      logError(`Error loading contact`, (response as any)?.error)
      notifyError(`Error loading contact`)
      return
    }
    setSelectedContact(response as ContactJson)
    setDrawerOpen(true)
    onUrlParamChange?.(`contact`, contactId)
  }

  function closeDrawer(): void {
    setSelectedContact(undefined)
    setDrawerOpen(false)
    onUrlParamChange?.(`contact`, null)
  }

  function reset(): void {
    setSelectedContact(undefined)
    setDrawerOpen(false)
    setSearchValue(``)
    setFilterValue(``)
  }

  return {
    contacts: filteredContacts,
    totalCount: allContacts.length,

    searchValue,
    setSearchValue,
    filterValue,
    setFilterValue,

    selectedContact,
    drawerOpen,
    handleContactClick,
    closeDrawer,
    setSelectedContact,

    nextContact: traversal.next,
    prevContact: traversal.prev,
    isNavigating: traversal.isNavigating,

    reset,
  }
}
